import type { MyFormSubmissionEntry } from '../api/publicFormsApi';

type SubmissionListEntryContentProps = {
  entry: MyFormSubmissionEntry;
  variant: 'menu' | 'dashboard';
  headerClassName?: string;
  lineClassName?: string;
  footerClassName?: string;
  /** When false, footer is omitted (render it outside, e.g. at card bottom). */
  showFooter?: boolean;
};

const FORM_TITLE_LINE_LABEL = 'Form title';

function isFormTitleLine(line: { label: string }) {
  return line.label === FORM_TITLE_LINE_LABEL;
}

export function SubmissionListEntryContent({
  entry,
  variant,
  headerClassName,
  lineClassName,
  footerClassName,
  showFooter = true
}: SubmissionListEntryContentProps) {
  const defaultHeaderClass =
    variant === 'menu'
      ? 'text-sm font-medium break-words'
      : 'text-base font-medium text-gray-900 dark:text-gray-100 line-clamp-2 break-words';
  const defaultLineClass =
    variant === 'menu'
      ? 'text-xs text-gray-500 dark:text-gray-400'
      : 'text-sm text-gray-500 dark:text-gray-400';
  const defaultFooterClass =
    variant === 'menu'
      ? 'text-xs text-gray-500 dark:text-gray-400'
      : 'text-sm text-gray-500 dark:text-gray-400';
  const headerClass = headerClassName ?? defaultHeaderClass;
  const lineClass = lineClassName ?? defaultLineClass;
  const footerClass = footerClassName ?? defaultFooterClass;

  const labelClass =
    variant === 'menu'
      ? 'font-semibold text-gray-600 dark:text-gray-300 shrink-0'
      : 'font-semibold text-gray-700 dark:text-gray-300 shrink-0';

  return (
    <div className="flex flex-col min-h-0 h-full">
      <div className="space-y-1 min-w-0 flex-1">
        {entry.headerLabel ? (
          <div
            className={`${headerClass} min-w-0 flex flex-wrap items-baseline gap-x-1 gap-y-0.5`}
            title={`${entry.headerLabel} ${entry.header}`}
          >
            <span className={labelClass}>{entry.headerLabel}</span>
            <span className="break-words min-w-0 flex-[1_1_0%] font-normal">{entry.header}</span>
          </div>
        ) : (
          <div className={headerClass}>{entry.header}</div>
        )}
        {(entry.lines ?? []).map((line, index) => {
          if (isFormTitleLine(line)) {
            return (
              <div
                key={`${line.label}-${index}`}
                className={`${lineClass} min-w-0 break-words`}
                title={line.value}
              >
                {line.value}
              </div>
            );
          }
          return (
            <div
              key={`${line.label}-${index}`}
              className={`${lineClass} min-w-0 flex flex-wrap items-baseline gap-x-1 gap-y-0.5`}
              title={`${line.label}: ${line.value}`}
            >
              <span className={labelClass}>{line.label}:</span>
              <span className="break-words min-w-0 flex-[1_1_0%]">{line.value}</span>
            </div>
          );
        })}
      </div>
      {showFooter && entry.footer ? (
        <div className={`${footerClass} mt-auto pt-3 break-words`}>
          {entry.footer}
        </div>
      ) : null}
    </div>
  );
}
